import { Inject, Injectable } from '@angular/core';
import { CUSTOMERENVIRONMENT, WINDOW } from '@app/common/app-tokens';
import { Utilities } from '@app/common/utilities';
import { AccountInfo, AuthenticationResult, AuthorizationUrlRequest, EndSessionRequest, InteractionRequiredAuthError, PopupRequest, PublicClientApplication, RedirectRequest, SilentRequest } from '@azure/msal-browser';
import { BaseAuthRequest, CommonAuthorizationUrlRequest } from '@azure/msal-common';
import { Environment } from '@environments/interface.environment';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap, map, tap } from 'rxjs/operators';
import { Platform } from '@angular/cdk/platform';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  public useRedirect: boolean;

  public readonly MSALObj: PublicClientApplication;

  private _accountInfo: AccountInfo;
  public get accountInfo(): AccountInfo {
    return this._accountInfo;
  }

  public loginRequest: Partial<CommonAuthorizationUrlRequest>;

  private protectedResourceEndpoints: Map<string, string[]>;

  public idTokenClaims = new Map<string, any>();

  constructor(@Inject(CUSTOMERENVIRONMENT) private environment: Environment, @Inject(WINDOW) private window: Window, private platform: Platform) {
    // If IE, recommendation is that you sign-in using Redirect APIs (https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/samples/msal-browser-samples/TypescriptTestApp2.0/src/index.ts#L7)
    this.useRedirect = Utilities.detectIE(this.window.navigator.userAgent) > -1 ||
    (this.platform.IOS && Utilities.isSafari(this.window)) ||
    this.environment.msalConfig.useRedirect;

    this.environment.msalConfig.cache = { ...{ storeAuthStateInCookie: this.useRedirect }, ...this.environment.msalConfig.cache };
    this.environment.msalConfig.auth.redirectUri = this.window.location.origin;

    this.MSALObj = new PublicClientApplication(this.environment.msalConfig);

    this.loginRequest = {
      scopes: ['openid', 'profile', 'offline_access', `${this.environment.msalConfig.auth.clientId}/.default`],
      extraQueryParameters: this.environment.msalConfig.extraQueryParameters,
      loginHint: this.environment.msalConfig.loginHint,
      domainHint: this.environment.msalConfig.domainHint,
    };

    this.protectedResourceEndpoints = new Map<string, string[]>(this.environment.msalConfig.protectedResourceScopeMap);

  }

  handleRedirect(): Observable<AuthenticationResult> {
    return from(this.MSALObj.handleRedirectPromise())
      .pipe(
        tap((authenticationResult: AuthenticationResult) => {
          this.setFields(authenticationResult);
        })
      );
  }

  private setFields(authenticationResult: AuthenticationResult) {
    if (authenticationResult) {
      Object.keys(authenticationResult.idTokenClaims)
        .forEach((key) => {
          this.idTokenClaims.set(key, authenticationResult.idTokenClaims[key]);
        });
      if (!this.accountInfo) {
        this._accountInfo = authenticationResult.account;
      }
    }
  }

  loginRedirect(request?: RedirectRequest): Observable<void> {
    return from(this.MSALObj.loginRedirect(request));
  }

  loginPopup(request?: PopupRequest): Observable<AuthenticationResult> {
    return from(this.MSALObj.loginPopup(request))
      .pipe(
        tap((authenticationResult: AuthenticationResult) => {
          this.setFields(authenticationResult);
        })
      );
  }

  logOut(request?: EndSessionRequest): Observable<void> {
    return from(this.MSALObj.logout(request));
  }

  acquireTokenSilent(request: SilentRequest): Observable<AuthenticationResult> {
    request.account = this.accountInfo;
    return from(this.MSALObj.acquireTokenSilent(request));
  }

  acquireTokenSilentWithFallBackToPopup(request: SilentRequest, fallbackRequest: BaseAuthRequest): Observable<AuthenticationResult> {
    return this.acquireTokenSilent(request)
      .pipe(
        catchError((err) => {
          if (err instanceof InteractionRequiredAuthError) {
            return this.acquireTokenPopup(fallbackRequest);
          }
          return throwError(err);
        }));
  }

  acquireTokenPopup(request: BaseAuthRequest): Observable<AuthenticationResult> {
    return from(this.MSALObj.acquireTokenPopup(request));
  }

  acquireTokenRedirect(request: RedirectRequest): Observable<AuthenticationResult> {
    request.loginHint = this.accountInfo.username;
    return this.handleRedirect()
      .pipe(
        mergeMap(authenticationResult => {
          if (authenticationResult) {
            return of(authenticationResult);
          } else {
            return from(this.MSALObj.acquireTokenRedirect(request))
              .pipe(
                map(() => null)
              );
          }
        })
      );
  }

  acquireTokenSilentWithFallBackToRedirect(request: SilentRequest, fallbackRequest: RedirectRequest): Observable<AuthenticationResult> {
    return this.acquireTokenSilent(request)
      .pipe(
        catchError((err) => {
          if (err instanceof InteractionRequiredAuthError) {
            return this.acquireTokenRedirect(fallbackRequest);
          }
          return throwError(err);
        }));
  }

  ssoSilent(request: AuthorizationUrlRequest): Observable<AuthenticationResult> {
    return from(this.MSALObj.ssoSilent(request));
  }

  getScopesForEndpoint(url: string): BaseAuthRequest {
    let request: BaseAuthRequest;
    for (const entry of Array.from(this.protectedResourceEndpoints.entries())) {
      const  resource = entry[0];
      const scopes = entry[1];
      if (url.indexOf(resource) > -1) {
        request = { scopes: scopes, authority: this.environment.msalConfig.auth.authority, correlationId: '' };
        break;
      }
    }
    return request;
  }
}
